import { useState, useEffect, useRef } from "react";
import { trackGA } from "../helpers";

interface MarketoProps {
    baseUrl?: string;
    munchkinId: string;
    marketoFormId: string;
    callback?: (form: any) => void;
    prePopulatedFields?: { [key: string]: string };
    onSuccess?: () => void;
}

const appendMarketoScript = ({ baseUrl, setScriptLoaded }: { baseUrl: string, setScriptLoaded: Function }) => {
    if ((window as any).MktoForms2) {
        return setScriptLoaded(true);
    } else {
        const script = document.createElement("script");
        script.src = `${baseUrl}/js/forms2/js/forms2.min.js`;
        script.onload = () => ((window as any).MktoForms2 ? setScriptLoaded(true) : null);
        document.body.appendChild(script);
    }
}

const appendZoomInfoScript = () => {
    // Skip if the script was already loaded
    if (document.querySelector('script[data-zoominfo="loaded"]')) {
        return;
    }

    // Note: We are ignoring TS errors as the script is obfuscated and 
    // copied/adapted from ZoomInfo provided script

    // Set the ZoomInfo ID in the window object
    (window as any)[(function (_vxQ, _7o) {
        var _gHS55 = '';
        for (var _Qfz88z = 0; _Qfz88z < _vxQ.length; _Qfz88z++) {
            // @ts-ignore
            var _602r = _vxQ[_Qfz88z].charCodeAt();
            _602r -= _7o;
            _gHS55 == _gHS55;
            _602r += 61;
            _602r != _Qfz88z;
            _602r %= 94;
            _602r += 33;
            _7o > 3;
            _gHS55 += String.fromCharCode(_602r)
        }
        return _gHS55
    })(atob('eWhvMzArJiQ1aiY6'), 31)] = '884373a26c1728485999';

    // Create ZoomInfo script element, set attributes and append to body
    const zi = document.createElement('script');
    zi.setAttribute('data-zoominfo', 'loaded');
    zi.setAttribute('id', 'zoominfo-script');
    zi.type = 'text/javascript';
    zi.async = true;
    zi.src = (function (_s4b, _9I) {
        var _8wE9o = '';
        for (var _whYo0y = 0; _whYo0y < _s4b.length; _whYo0y++) {
            // @ts-ignore
            _N9Er != _whYo0y;
            // @ts-ignore
            var _N9Er = _s4b[_whYo0y].charCodeAt();
            _N9Er -= _9I;
            _N9Er += 61;
            _9I > 4;
            _N9Er %= 94;
            _N9Er += 33;
            _8wE9o == _8wE9o;
            _8wE9o += String.fromCharCode(_N9Er)
        }
        return _8wE9o
    })(atob('Ljo6NjleU1MwOVJAL1E5KTgvNjo5Uik1M1NAL1E6Jy1SMDk='), 36);

    document.body.appendChild(zi);
    console.info('ZoomInfo script loaded');
    
    // Added trigger for fcRetrigger
    setTimeout(() => {
        if ((window as any).fcRetrigger) {
            (window as any).fcRetrigger();
            console.info('fcRetrigger triggered');
        }
    }, 10);
    return zi;
}

export const useMarketo = ({ baseUrl = '//page.oneocean.com', munchkinId, marketoFormId, prePopulatedFields, onSuccess }: MarketoProps) => {
    const [scriptLoaded, setScriptLoaded] = useState(false);
    const formRef = useRef<any>(null);
    const zoomInfoScriptRef = useRef<HTMLScriptElement | null>(null);

    useEffect(() => {
        if (scriptLoaded) {
            if (!(window as any).MktoForms2) return;

            // Load the Marketo form
            formRef.current = (window as any).MktoForms2.loadForm(baseUrl, munchkinId, marketoFormId, (form: any) => {
                const marketoLoader = document.getElementById('marketoLoader');
                if (marketoLoader) {
                    marketoLoader.style.display = 'none';
                }

                const formElement = form.getFormElem();

                // Pre populate form fields
                if (prePopulatedFields) {
                    form.vals({ ...prePopulatedFields });
                }

                const utmField = document.querySelector('input[name="Driver_ID_Most_Recent__c"]') as HTMLInputElement;
                if (utmField) {
                    const cookies = document.cookie.split('; ');
                    let urlParamsCookie = '';

                    cookies.forEach(cookie => {
                        const [key, value] = cookie.split('=');
                        if (key === 'urlParams') {
                            urlParamsCookie = decodeURIComponent(value);
                        }
                    });

                    if (urlParamsCookie) {
                        try {
                            const params = JSON.parse(urlParamsCookie);
                            console.info(params);
                            if (params.sfcid) {
                                utmField.value = params.sfcid;
                            }
                        } catch (error) {
                            console.error('Error parsing URL params cookie:', error);
                        }
                    }
                }

                // You cant submit over localhost so you can use onSubmit to test.
                // form.onSubmit(() => {
                //     formElement.hide();
                //     document.getElementById(`success-${marketoFormId}`)!.style.display = 'block';
                //     trackGA('marketo_submission', { title: marketoFormId });
                //     document.cookie = 'urlParams=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
                //     if (onSuccess) onSuccess();
                //     return false;
                // });

                form.onSuccess(() => {
                    formElement.hide();
                    const successElement = document.getElementById(`success-${marketoFormId}`);
                    if (successElement) {
                        successElement.style.display = 'block';
                    }
                    trackGA('marketo_submission', { title: marketoFormId });
                    document.cookie = 'urlParams=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
                    if (onSuccess) onSuccess();
                    return false;
                });
            });

            // Removes style attribute and loads ZoomInfo after form is fully rendered
            (window as any).MktoForms2.whenRendered((form: any) => {
                const formElement = form.getFormElem();
                formElement[0].removeAttribute('style');

                // Load ZoomInfo script after form is rendered and store reference
                if (!zoomInfoScriptRef.current) {
                    setTimeout(() => {
                        zoomInfoScriptRef.current = appendZoomInfoScript() || null;
                    }, 1000);
                }
            });
        } else {
            appendMarketoScript({ baseUrl, setScriptLoaded });
        }

        // Cleanup function when component unmounts or before re-running effect
        return () => {
            // Remove ZoomInfo script when navigating or unmounting
            if (zoomInfoScriptRef.current) {
                zoomInfoScriptRef.current.remove();
                zoomInfoScriptRef.current = null;
                console.info('ZoomInfo script removed');
            }

            // Destroy the Marketo form instance if it exists
            if (formRef.current && (window as any).MktoForms2) {
                try {
                    // Check if the form element still exists in the DOM
                    const formElement = document.getElementById(`mktoForm_${marketoFormId}`);
                    if (formElement) {
                        // Access the MktoForms2 instance and destroy it if possible
                        (window as any).MktoForms2.getForm(marketoFormId)?.destroy();
                        console.info(`Marketo form ${marketoFormId} destroyed`);
                    }
                } catch (error) {
                    console.error('Error cleaning up Marketo form:', error);
                }

                formRef.current = null;
            }
        };
    }, [baseUrl, marketoFormId, munchkinId, prePopulatedFields, scriptLoaded]);

    return {
        isLoaded: scriptLoaded,
        formInstance: formRef.current
    };
}